import { useEffect, useMemo, useState } from "react";
import { EyeOff, Eye, Pin, PinOff, Clock, AlertTriangle, ShieldCheck, ShieldAlert, ShieldX } from "lucide-react";
import {
  hideResponse,
  pinResponse,
  reclassifyResponse,
  getModerationReasons,
} from "../lib/api";
import type { ModerationCategory, Question, Response } from "../lib/types";
import { AnswerDisplay } from "./AnswerDisplay";

interface Props {
  responses: Response[];
  /** When present, typed-question answers (rating / option / side) are rendered
   *  in place of (or above) the free-text body. */
  question?: Question | null;
}

type Filter = "all" | "block" | "borderline" | "pending";

const CATEGORY_LABEL: Record<ModerationCategory, string> = {
  obscenity: "Obszönität",
  hate: "Hassrede",
  personal_attack: "Persönlicher Angriff",
  custom: "Custom",
};

const CHIP_BASE =
  "px-2.5 py-0.5 rounded-full text-[11px] uppercase tracking-[0.14em] font-medium transition-colors ease-rc-out";

export default function ResponseModerator({ responses, question }: Props) {
  const [filter, setFilter] = useState<Filter>("all");
  const [reasons, setReasons] = useState<Record<string, string>>({});

  // Fetch moderation reasons for blocked responses
  const blockIds = useMemo(
    () => responses.filter((r) => r.moderation_status === "block").map((r) => r.id),
    [responses],
  );
  const blockKey = blockIds.join(",");

  useEffect(() => {
    if (blockIds.length === 0) {
      setReasons({});
      return;
    }
    let cancelled = false;
    getModerationReasons(blockIds).then((map) => {
      if (!cancelled) setReasons(map);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockKey]);

  const counts = useMemo(() => {
    let block = 0;
    let borderline = 0;
    let pending = 0;
    for (const r of responses) {
      if (r.moderation_status === "pending") pending++;
      else if (r.moderation_status === "block" && !r.moderation_overridden) block++;
      else if (r.moderation_status === "borderline" && !r.moderation_overridden) borderline++;
    }
    return { all: responses.length, block, borderline, pending };
  }, [responses]);

  const filtered = useMemo(() => {
    if (filter === "block") {
      return responses.filter((r) => r.moderation_status === "block" && !r.moderation_overridden);
    }
    if (filter === "borderline") {
      return responses.filter((r) => r.moderation_status === "borderline" && !r.moderation_overridden);
    }
    if (filter === "pending") {
      return responses.filter((r) => r.moderation_status === "pending");
    }
    return responses;
  }, [responses, filter]);

  const sorted = useMemo(
    () =>
      [...filtered].sort((a, b) => {
        if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
        return new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime();
      }),
    [filtered],
  );

  const filterStrip = responses.length > 0 ? (
    <div className="flex items-center gap-1 mb-3 flex-wrap">
      <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
        Alle ({counts.all})
      </FilterChip>
      <span className="text-rc-slate text-[11px]">·</span>
      <FilterChip active={filter === "block"} onClick={() => setFilter("block")}>
        Blockiert ({counts.block})
      </FilterChip>
      <span className="text-rc-slate text-[11px]">·</span>
      <FilterChip active={filter === "borderline"} onClick={() => setFilter("borderline")}>
        Grenzwertig ({counts.borderline})
      </FilterChip>
      <span className="text-rc-slate text-[11px]">·</span>
      <FilterChip active={filter === "pending"} onClick={() => setFilter("pending")}>
        Ausstehend ({counts.pending})
      </FilterChip>
    </div>
  ) : null;

  if (sorted.length === 0) {
    const emptyCopy =
      filter === "block"
        ? "Keine blockierten Antworten."
        : filter === "borderline"
          ? "Keine grenzwertigen Antworten."
          : filter === "pending"
            ? "Keine ausstehenden Antworten."
            : "Noch keine Antworten. Öffne die Frage, um zu starten.";
    return (
      <div>
        {filterStrip}
        <div className="text-center py-16 bg-rc-paper rounded-rc-lg border border-dashed border-rc-fog">
          <p className="text-rc-slate text-[14px]">{emptyCopy}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {filterStrip}
      <ul className="flex flex-col gap-2">
        {sorted.map((r) => {
          const cardCls = r.is_hidden
            ? "bg-rc-mist border-rc-fog opacity-60"
            : r.is_pinned
              ? "bg-rc-berry/6 border-rc-berry/30"
              : "bg-rc-paper border-rc-fog hover:border-rc-charcoal/25";

          const isPending = r.moderation_status === "pending";
          const isBorderline = r.moderation_status === "borderline";
          const isBlock = r.moderation_status === "block";
          const isOverridden = r.moderation_overridden;
          const categoryLabel = r.moderation_category ? CATEGORY_LABEL[r.moderation_category] : "";
          const reason = reasons[r.id];

          return (
            <li
              key={r.id}
              className={`flex items-start gap-3 p-4 rounded-rc-lg border transition-colors ease-rc-out ${cardCls}`}
            >
              <div className="flex-1 min-w-0 text-[14px] leading-[1.55]">
                <AnswerDisplay question={question ?? null} response={r} surface="light" size="comfortable" />
                {(isPending || isBorderline || isBlock) && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    {isPending && (
                      <span
                        className={`${CHIP_BASE} inline-flex items-center gap-1 bg-rc-mist text-rc-slate border border-rc-fog`}
                      >
                        <Clock className="w-3 h-3" />
                        Prüfung läuft…
                      </span>
                    )}
                    {isBorderline && !isOverridden && (
                      <span
                        title={reason ?? "Grenzwertige Antwort — wartet auf Freigabe"}
                        className={`${CHIP_BASE} inline-flex items-center gap-1 bg-rc-lime/20 text-rc-black border border-rc-lime/40`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        {r.borderline_until == null ? "Grenzwertig · zurückgehalten" : "Grenzwertig"}
                      </span>
                    )}
                    {isBlock && !isOverridden && (
                      <span
                        title={reason ?? "Blockiert durch Moderation"}
                        className={`${CHIP_BASE} inline-flex items-center gap-1 bg-rc-berry/15 text-rc-berry border border-rc-berry/30`}
                      >
                        <AlertTriangle className="w-3 h-3" />
                        Blockiert · {categoryLabel}
                      </span>
                    )}
                    {isOverridden && (
                      <span
                        className={`${CHIP_BASE} inline-flex items-center gap-1 bg-rc-mist text-rc-charcoal border border-rc-fog`}
                      >
                        Manuell klassifiziert
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex gap-1 flex-shrink-0 items-center">
                {/* Reclassify control: 3-state toggle clean / borderline / block */}
                <ReclassifyControl responseId={r.id} current={r.moderation_status} />
                <button
                  type="button"
                  onClick={() => pinResponse(r.id, !r.is_pinned)}
                  aria-label={r.is_pinned ? "Antwort lösen" : "Antwort anheften"}
                  title={r.is_pinned ? "Lösen" : "Anheften"}
                  className={`p-1.5 rounded-full transition-colors ${
                    r.is_pinned
                      ? "text-rc-berry hover:text-rc-berry-700 bg-rc-berry/8"
                      : "text-rc-slate hover:text-rc-black hover:bg-rc-mist"
                  }`}
                >
                  {r.is_pinned ? <PinOff className="w-3.5 h-3.5" /> : <Pin className="w-3.5 h-3.5" />}
                </button>
                <button
                  type="button"
                  onClick={() => hideResponse(r.id, !r.is_hidden)}
                  aria-label={r.is_hidden ? "Auf Bühne zeigen" : "Von Bühne verbergen"}
                  title={r.is_hidden ? "Auf Bühne zeigen" : "Verbergen"}
                  className={`p-1.5 rounded-full transition-colors ${
                    r.is_hidden
                      ? "text-rc-berry hover:text-rc-berry-700 bg-rc-berry/8"
                      : "text-rc-slate hover:text-rc-black hover:bg-rc-mist"
                  }`}
                >
                  {r.is_hidden ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ─── Reclassify control ────────────────────────────────────────────────────

function ReclassifyControl({
  responseId,
  current,
}: {
  responseId: string;
  current: string;
}) {
  const [busy, setBusy] = useState(false);

  const handle = async (status: "clean" | "borderline" | "block") => {
    if (busy) return;
    setBusy(true);
    try {
      await reclassifyResponse(responseId, status);
    } catch (err) {
      console.error("[ReclassifyControl] failed", err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex gap-0.5 ${busy ? "opacity-50 pointer-events-none" : ""}`}>
      <button
        type="button"
        onClick={() => handle("clean")}
        aria-label="Als sauber markieren"
        title="Freigeben (clean)"
        className={`p-1.5 rounded-full transition-colors ${
          current === "clean"
            ? "text-rc-lime-700 bg-rc-lime/20"
            : "text-rc-slate hover:text-rc-lime-700 hover:bg-rc-lime/15"
        }`}
      >
        <ShieldCheck className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => handle("borderline")}
        aria-label="Als grenzwertig markieren"
        title="Grenzwertig (borderline)"
        className={`p-1.5 rounded-full transition-colors ${
          current === "borderline"
            ? "text-rc-charcoal bg-rc-stone/20"
            : "text-rc-slate hover:text-rc-charcoal hover:bg-rc-mist"
        }`}
      >
        <ShieldAlert className="w-3.5 h-3.5" />
      </button>
      <button
        type="button"
        onClick={() => handle("block")}
        aria-label="Als blockiert markieren"
        title="Blockieren (block)"
        className={`p-1.5 rounded-full transition-colors ${
          current === "block"
            ? "text-rc-berry bg-rc-berry/10"
            : "text-rc-slate hover:text-rc-berry hover:bg-rc-berry/8"
        }`}
      >
        <ShieldX className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${CHIP_BASE} ${
        active
          ? "bg-rc-black text-rc-cream border border-rc-black"
          : "bg-rc-paper text-rc-slate border border-rc-fog hover:text-rc-black hover:border-rc-charcoal/25"
      }`}
    >
      {children}
    </button>
  );
}
